import { useState } from "react";
import Modal from "@/components/common/Modal";
import { useApp } from "@/contexts/AppContext";
import { AnalyticsFiltersDrawer } from "@/features/analytics/components/AnalyticsFiltersDrawer";
import {
  cloneAnalyticsFilters,
  createEmptyAnalyticsFilters,
  countActiveAnalyticsFilters,
} from "@/features/analytics/utils/analytics";

const filtersToPredefinedFilter = (filters) => {
  const payload = {};
  for (const key of Object.keys(filters)) {
    const value = filters[key];
    if (Array.isArray(value) && value.length > 0) {
      payload[key] = [...value];
    }
  }
  return payload;
};

export default function AddUserModal({ isOpen, onClose }) {
  const { addUser, campaigns, availableChannels, availableCategories } =
    useApp();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [predefinedFilters, setPredefinedFilters] = useState(
    createEmptyAnalyticsFilters(),
  );
  const [draftFilters, setDraftFilters] = useState(
    createEmptyAnalyticsFilters(),
  );
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setUsername("");
    setPassword("");
    setRole("user");
    setPredefinedFilters(createEmptyAnalyticsFilters());
    setDraftFilters(createEmptyAnalyticsFilters());
    setError("");
  };

  const handleSubmit = async () => {
    if (!username || !password) {
      setError("Username and password are required");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const predefined =
        role === "provider"
          ? filtersToPredefinedFilter(predefinedFilters)
          : null;
      await addUser(username, password, role, predefined);
      resetForm();
      onClose();
    } catch (err) {
      setError(err.message || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const activeScopeCount = countActiveAnalyticsFilters(predefinedFilters);

  const footer = (
    <>
      <button
        className="btn btn-secondary"
        type="button"
        onClick={() => {
          resetForm();
          onClose();
        }}
        disabled={loading}
      >
        Cancel
      </button>
      <button
        className="btn btn-primary"
        type="button"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? "Creating..." : "Create User"}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        resetForm();
        onClose();
      }}
      title="Create New User"
      footer={footer}
    >
      {error && (
        <div className="form-error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}
      <div className="form-group-modal">
        <label htmlFor="new-username">Username *</label>
        <input
          id="new-username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="Enter username"
          autoComplete="username"
          disabled={loading}
        />
      </div>
      <div className="form-group-modal">
        <label htmlFor="new-password">Password *</label>
        <input
          id="new-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter password"
          autoComplete="new-password"
          disabled={loading}
        />
      </div>
      <div className="form-group-modal">
        <label htmlFor="new-role">Role *</label>
        <select
          id="new-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={loading}
        >
          <option value="user">User</option>
          <option value="admin">Admin</option>
          <option value="provider">Provider</option>
        </select>
      </div>
      {role === "provider" ? (
        <div className="form-group-modal">
          <label>Predefined scope (provider sees only matching data)</label>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setDraftFilters(cloneAnalyticsFilters(predefinedFilters));
                setIsFiltersOpen(true);
              }}
              disabled={loading}
            >
              Edit scope
            </button>
            <span
              style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}
            >
              {activeScopeCount > 0
                ? `${activeScopeCount} filter group(s) set`
                : "No scope set — provider will see nothing until scope is set"}
            </span>
          </div>
        </div>
      ) : null}

      <AnalyticsFiltersDrawer
        isOpen={isFiltersOpen}
        value={draftFilters}
        campaigns={campaigns || []}
        channels={availableChannels || []}
        categories={availableCategories || []}
        onChange={setDraftFilters}
        onClose={() => setIsFiltersOpen(false)}
        onApply={() => {
          setPredefinedFilters(cloneAnalyticsFilters(draftFilters));
          setIsFiltersOpen(false);
        }}
        onReset={() => setDraftFilters(createEmptyAnalyticsFilters())}
      />
    </Modal>
  );
}
